import type { CodeLanguage, CodeObjectV2 } from "@bezpart-board/shared";

export const CODE_SOURCE_MAX = 24_000;
export const CODE_STDOUT_MAX = 8_000;
export const CODE_RUN_TIMEOUT_MS = 60_000;

export type CodeState = {
  language: CodeLanguage;
  source: string;
  html: string;
  css: string;
  js: string;
  stdout: string;
  /** Счётчик запусков: по его смене превью HTML обновляется у всех участников. */
  runSeq: number;
};

/** Python живёт в source, JavaScript — в js (не путаем примеры при смене вкладки). */
export const CODE_DEFAULTS: CodeState = {
  language: "python",
  source: 'print("Привет с доски!")\na = input("Как тебя зовут? ")\nprint("Привет,", a)\n',
  html: "<h1>Привет!</h1>\n<p>Правите HTML и CSS — превью справа.</p>\n",
  css: "body {\n  font-family: system-ui, sans-serif;\n  padding: 16px;\n  background: #f8fafc;\n  color: #0f172a;\n}\nh1 { color: #0f766e; margin-top: 0; }\n",
  js: 'import { chunk } from "lodash";\nconsole.log(chunk([1, 2, 3, 4, 5, 6], 2));\n',
  stdout: "",
  runSeq: 0,
};

export type CodeAction =
  | { type: "setLanguage"; language: CodeLanguage }
  | { type: "setSource"; source: string }
  | { type: "setHtml"; html: string }
  | { type: "setCss"; css: string }
  | { type: "setJs"; js: string }
  | { type: "setStdout"; stdout: string }
  /** Кто-то нажал «Запуск» / «Обновить превью». */
  | { type: "run" };

function clampStr(s: string, max: number): string {
  if (s.length <= max) return s;
  return s.slice(0, max);
}

export function readCodeState(obj: Partial<CodeObjectV2> | null | undefined): CodeState {
  const d = CODE_DEFAULTS;
  const language =
    obj?.language === "javascript" || obj?.language === "web" || obj?.language === "python"
      ? obj.language
      : d.language;
  return {
    language,
    source: typeof obj?.source === "string" ? obj.source : d.source,
    html: typeof obj?.html === "string" ? obj.html : d.html,
    css: typeof obj?.css === "string" ? obj.css : d.css,
    js: typeof obj?.js === "string" ? obj.js : d.js,
    stdout: typeof obj?.stdout === "string" ? obj.stdout : d.stdout,
    runSeq:
      typeof obj?.runSeq === "number" && Number.isFinite(obj.runSeq) && obj.runSeq >= 0
        ? Math.floor(obj.runSeq)
        : 0,
  };
}

/** Текст редактора для текущего языка. */
export function activeEditorValue(state: CodeState): string {
  if (state.language === "javascript") return state.js;
  if (state.language === "python") return state.source;
  return state.html;
}

export function applyCodeAction(state: CodeState, action: CodeAction): CodeState {
  switch (action.type) {
    case "setLanguage":
      return { ...state, language: action.language };
    case "setSource":
      return { ...state, source: clampStr(action.source, CODE_SOURCE_MAX) };
    case "setHtml":
      return { ...state, html: clampStr(action.html, CODE_SOURCE_MAX) };
    case "setCss":
      return { ...state, css: clampStr(action.css, CODE_SOURCE_MAX) };
    case "setJs":
      return { ...state, js: clampStr(action.js, CODE_SOURCE_MAX) };
    case "setStdout":
      return { ...state, stdout: clampStr(action.stdout, CODE_STDOUT_MAX) };
    case "run":
      return { ...state, runSeq: state.runSeq + 1 };
    default:
      return state;
  }
}

export function codeStateToPatch(state: CodeState): Record<string, unknown> {
  return {
    language: state.language,
    source: state.source,
    html: state.html,
    css: state.css,
    js: state.js,
    stdout: state.stdout,
    runSeq: state.runSeq,
  };
}

/**
 * Только изменившиеся поля. Раньше на каждую букву уезжали все поля виджета —
 * и конфликтовали с правкой соседнего поля у другого участника.
 */
export function codeStatePatch(prev: CodeState, next: CodeState): Record<string, unknown> {
  const full = codeStateToPatch(next);
  const before = codeStateToPatch(prev);
  const patch: Record<string, unknown> = {};
  for (const key of Object.keys(full)) {
    if (full[key] !== before[key]) patch[key] = full[key];
  }
  return patch;
}

export function languageLabel(lang: CodeLanguage): string {
  if (lang === "python") return "Python";
  if (lang === "javascript") return "JavaScript";
  return "HTML / CSS";
}
